import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { BaseClickable, palette, SolvTheme, Stack, Text } from '@solvhealth/jigsaw-v1';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { SeasonalServices } from '~/components/AccountGetsMarried/components/GetCare/components/SeasonalServices';
import VerticalModalities from '~/components/AccountGetsMarried/components/GetCare/components/ModalitiesOfCare/VerticalModalities';
import BookFluShot from '~/components/AccountGetsMarried/components/GetCare/components/NavTiles/components/BookFluShot';
import BookLabTest from '~/components/AccountGetsMarried/components/GetCare/components/NavTiles/components/BookLabTest';
import Triage from '~/components/AccountGetsMarried/components/GetCare/components/NavTiles/components/Triage/Triage';
import { ServicesSection } from '~/components/AccountGetsMarried/components/GetCare/components/Services';
import useUpcomingAppointment from '~/components/AccountGetsMarried/hooks/useUpcomingAppointment';
import { useCareTeam } from '~/components/CareTeam/hooks/useCareTeam';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  ACCOUNT_HOME_NEW_SERVICES_V1_ON,
  SOLV_PLUS_ENTRYPOINT_ACCOUNT_HOME,
  TRIAGE_ENTRYPOINT_ACCOUNT_HOME,
  TRIAGE_ENTRYPOINT_VISIBLE,
} from '~/core/analytics/events';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import { isActiveMembership } from '~/core/util/account';
import { useAccount } from '~/hooks/account/useAccount';
import useUserPosition, { PositionTrigger } from '~/hooks/geo/useUserPosition';
import useShouldShowPremiumVisitEntrypoints from '~/hooks/membership/useShouldShowPremiumVisitEntrypoints';
import useToggleRuntimeModal from '~/hooks/runtime/useToggleRuntimeModal';
import { useTriageEnabled } from '~/hooks/useTriageEnabled';
import { IS_FLU_SEASON } from '../../../../../../config';
import { biggerThanOrEqualTo } from '../../../../../../core/util/styledComponents';
import { LOCATION_SELECTOR_MODAL } from '../../../UserGeoLocationSelector';
import BookAgain from '../BookAgain';
import { PremiumVisitSection } from '../PremiumVisitSection';
import BookCovidTest from './components/BookCovidTest';
import BookUrgentCare from './components/BookUrgentCare';
import BookVideoVisit from './components/BookVideoVisit';
import Search from './components/Search';

const Base = styled.div.withConfig({ componentId: 'sc-1vco67o-0' })`
  background-color: white;
  padding-bottom: 24px;
  border-top: solid 1px ${palette.gray[200]};
`;

const Content = styled.div.withConfig({ componentId: 'sc-1vco67o-1' })`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${biggerThanOrEqualTo.sm`
    display: grid;
    grid-gap: 24px;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: minmax(100px, auto);
    padding-left: 24px;
    padding-right: 24px;
    max-width: ${SolvTheme.breakpoints.lg};
    margin: 0 auto;
    box-sizing: border-box;
  `}
  ${biggerThanOrEqualTo.lg`
    grid-template-columns: repeat(4, 1fr);
  `}
`;

type Props = {
  userProfileId: string;
};

type LayoutProps = {
  userProfileId: string;
  showSolvPremium: boolean;
};
const LayoutWithoutExpandedServices = ({ userProfileId, showSolvPremium }: LayoutProps) => {
  const location = useSelector((state) => state.searchPreferences.location);
  const { position } = useUserPosition(PositionTrigger.IPOnly);
  const { t } = useTranslation(['account', 'common']);
  const locationLabel = location?.label ?? position.result?.label ?? t('common:currentLocation');

  const triageEnabled = useTriageEnabled();
  const isFluSeason = IS_FLU_SEASON;

  const [_, toggleLocationModal] = useToggleRuntimeModal(LOCATION_SELECTOR_MODAL);

  useEffect(() => {
    analyticsTrackEvent(TRIAGE_ENTRYPOINT_VISIBLE, {
      entrypoint: TRIAGE_ENTRYPOINT_ACCOUNT_HOME,
      visible: triageEnabled,
    });
  }, [triageEnabled]);

  return (
    <Stack maxWidth={SolvTheme.breakpoints.lg} mx="auto" pb={6} pt={4} space={4} width="100%">
      <BookAgain />

      {showSolvPremium && <PremiumVisitSection userProfileId={userProfileId} />}

      <Stack space={2} width="100%">
        <Stack alignItems="flex-start" px={3}>
          <Text variant="h3">{t('account:home.findCare.title')}</Text>
          <BaseClickable
            endIcon={<ExpandMoreIcon htmlColor={palette.gray[600]} style={{ fontSize: '14px' }} />}
            flexGrow={0}
            onClick={toggleLocationModal}
          >
            <Text>{locationLabel}</Text>
          </BaseClickable>
        </Stack>

        <Content>
          <BookUrgentCare />
          <BookVideoVisit />
          {triageEnabled && <Triage entrypoint={TRIAGE_ENTRYPOINT_ACCOUNT_HOME} />}
          <BookCovidTest />
          {isFluSeason && <BookFluShot userProfileId={userProfileId} />}
          <BookLabTest />
          <Search />
        </Content>
      </Stack>
    </Stack>
  );
};

export const ExpandedServicesLayout = ({ userProfileId, showSolvPremium }: LayoutProps) => {
  return (
    <Stack maxWidth={SolvTheme.breakpoints.lg} mx="auto" pb={4} pt={4} space={4} width="100%">
      <BookAgain />

      {showSolvPremium && <PremiumVisitSection userProfileId={userProfileId} />}

      <VerticalModalities />

      <ServicesSection />

      <SeasonalServices userProfileId={userProfileId} />
    </Stack>
  );
};

const NavTiles = (props: Props) => {
  const { userProfileId } = props;
  const { plus980NewServices } = useLDFlags();

  const { appointment } = useUpcomingAppointment(userProfileId);
  const { allLocations } = useCareTeam();

  // Solv Premium
  const account = useAccount();
  const entrypointId = SOLV_PLUS_ENTRYPOINT_ACCOUNT_HOME;
  const showSolvPremiumVisitEntrypointAtTop =
    isActiveMembership(account) || (!appointment && isEmpty(allLocations));
  const showSolvPremium = useShouldShowPremiumVisitEntrypoints({
    showToExistingMembers: true,
    overrideExcludedLocations: false,
    entrypointId,
    extraTrackingData: {
      placement: showSolvPremiumVisitEntrypointAtTop ? 'top' : 'bottom',
    },
  });

  useEffect(() => {
    analyticsTrackEvent(ACCOUNT_HOME_NEW_SERVICES_V1_ON, {
      on: plus980NewServices,
    });
  }, [plus980NewServices]);

  return (
    <Base>
      {plus980NewServices ? (
        <ExpandedServicesLayout showSolvPremium={showSolvPremium} userProfileId={userProfileId} />
      ) : (
        <LayoutWithoutExpandedServices
          showSolvPremium={showSolvPremium}
          userProfileId={userProfileId}
        />
      )}
    </Base>
  );
};

export default NavTiles;
